import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const MIME_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const escapeXml = (value) =>
    String(value ?? '')
        .split('')
        .filter((char) => {
            const code = char.charCodeAt(0);
            return code === 9 || code === 10 || code === 13 || code >= 32;
        })
        .join('')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');

const colRef = (index) => {
    let ref = '';
    let n = index + 1;
    while (n > 0) {
        const rem = (n - 1) % 26;
        ref = String.fromCharCode(65 + rem) + ref;
        n = Math.floor((n - 1) / 26);
    }
    return ref;
};

const cellRef = (colIndex, rowIndex) => `${colRef(colIndex)}${rowIndex + 1}`;

const normalizeCell = (value) => {
    if (value === null || value === undefined) return '';
    if (value instanceof Date) return value.toISOString();
    if (Array.isArray(value)) return value.filter(Boolean).join('\n');
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    return value;
};

const cellXml = (value, colIndex, rowIndex, styleId) => {
    const normalized = normalizeCell(value);
    const ref = cellRef(colIndex, rowIndex);
    const style = styleId ? ` s="${styleId}"` : '';

    if (typeof normalized === 'number' && Number.isFinite(normalized)) {
        return `<c r="${ref}"${style}><v>${normalized}</v></c>`;
    }

    const text = escapeXml(normalized);
    if (text === '') return `<c r="${ref}"${style}/>`;
    return `<c r="${ref}" t="inlineStr"${style}><is><t xml:space="preserve">${text}</t></is></c>`;
};

const rowXml = (row, rowIndex, isHeader) => {
    const height = isHeader ? ' ht="24" customHeight="1"' : '';
    const cells = row.map((value, colIndex) => cellXml(value, colIndex, rowIndex, isHeader ? 1 : 0)).join('');
    return `<row r="${rowIndex + 1}"${height}>${cells}</row>`;
};

const estimateWidths = (headers, rows) =>
    headers.map((header, colIndex) => {
        const headerLength = String(header || '').length;
        const maxCellLength = rows.reduce((max, row) => {
            const value = normalizeCell(row[colIndex]);
            const longestLine = String(value || '')
                .split('\n')
                .reduce((lineMax, line) => Math.max(lineMax, line.length), 0);
            return Math.max(max, longestLine);
        }, headerLength);
        return Math.min(Math.max(maxCellLength + 2, 10), 55);
    });

const sheetXml = ({ headers, rows }) => {
    const safeHeaders = headers.length ? headers : ['Dados'];
    const safeRows = rows || [];
    const allRows = [safeHeaders, ...safeRows];
    const lastCell = cellRef(safeHeaders.length - 1, allRows.length - 1);
    const widths = estimateWidths(safeHeaders, safeRows);
    const cols = widths
        .map((width, index) => `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`)
        .join('');
    const sheetData = allRows.map((row, rowIndex) => rowXml(row, rowIndex, rowIndex === 0)).join('');

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
    <dimension ref="A1:${lastCell}"/>
    <sheetViews>
        <sheetView workbookViewId="0">
            <pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>
        </sheetView>
    </sheetViews>
    <sheetFormatPr defaultRowHeight="15"/>
    <cols>${cols}</cols>
    <sheetData>${sheetData}</sheetData>
    <autoFilter ref="A1:${lastCell}"/>
</worksheet>`;
};

const safeSheetName = (name, index) => {
    const cleaned = String(name || `Planilha ${index + 1}`)
        .replace(/[\\/?*[\]:]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return escapeXml((cleaned || `Planilha ${index + 1}`).slice(0, 31));
};

const contentTypesXml = (sheetCount) => {
    const sheetOverrides = Array.from({ length: sheetCount }, (_, index) =>
        `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
    ).join('');

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
    <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
    <Default Extension="xml" ContentType="application/xml"/>
    <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
    <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
    <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>
    <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>
    ${sheetOverrides}
</Types>`;
};

const workbookXml = (sheets) => {
    const sheetEntries = sheets.map((sheet, index) =>
        `<sheet name="${safeSheetName(sheet.name, index)}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`
    ).join('');

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
    <workbookPr date1904="false"/>
    <sheets>${sheetEntries}</sheets>
</workbook>`;
};

const workbookRelsXml = (sheetCount) => {
    const sheetRels = Array.from({ length: sheetCount }, (_, index) =>
        `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`
    ).join('');

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
    ${sheetRels}
    <Relationship Id="rId${sheetCount + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;
};

const rootRelsXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
    <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
    <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>
    <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>
</Relationships>`;

const stylesXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
    <fonts count="2">
        <font><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>
        <font><b/><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>
    </fonts>
    <fills count="2">
        <fill><patternFill patternType="none"/></fill>
        <fill><patternFill patternType="gray125"/></fill>
    </fills>
    <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
    <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
    <cellXfs count="2">
        <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
        <xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>
    </cellXfs>
    <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

const coreXml = () => {
    const now = new Date().toISOString();
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:dcmitype="http://purl.org/dc/dcmitype/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
    <dc:creator>Vector Tracker</dc:creator>
    <cp:lastModifiedBy>Vector Tracker</cp:lastModifiedBy>
    <dcterms:created xsi:type="dcterms:W3CDTF">${now}</dcterms:created>
    <dcterms:modified xsi:type="dcterms:W3CDTF">${now}</dcterms:modified>
</cp:coreProperties>`;
};

const appXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">
    <Application>Vector Tracker</Application>
</Properties>`;

export const downloadXlsx = async (sheets, fileName) => {
    const validSheets = (sheets || []).filter(sheet => sheet && Array.isArray(sheet.headers));
    if (validSheets.length === 0) {
        throw new Error('Nenhuma planilha válida para exportar.');
    }

    const zip = new JSZip();
    zip.file('[Content_Types].xml', contentTypesXml(validSheets.length));
    zip.folder('_rels').file('.rels', rootRelsXml());
    zip.folder('docProps').file('core.xml', coreXml());
    zip.folder('docProps').file('app.xml', appXml());

    const xl = zip.folder('xl');
    xl.file('workbook.xml', workbookXml(validSheets));
    xl.file('styles.xml', stylesXml());
    xl.folder('_rels').file('workbook.xml.rels', workbookRelsXml(validSheets.length));

    const worksheets = xl.folder('worksheets');
    validSheets.forEach((sheet, index) => {
        worksheets.file(`sheet${index + 1}.xml`, sheetXml(sheet));
    });

    const blob = await zip.generateAsync({ type: 'blob', mimeType: MIME_XLSX });
    saveAs(blob, fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`);
};
